import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { CreditCard, MoreHorizontal, CheckCircle, DollarSign, Clock, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', class: 'bg-orange-100 text-orange-800' },
  approved: { label: 'Approved', class: 'bg-blue-100 text-blue-800' },
  paid: { label: 'Paid', class: 'bg-green-100 text-green-800' }
};

const PAYMENT_METHODS = [
  { value: 'check', label: 'Check' },
  { value: 'ach', label: 'ACH Transfer' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

const Payables = () => {
  const { getAuthHeaders } = useAuth();
  const [payables, setPayables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'check',
    reference_number: '',
    notes: ''
  });

  const fetchPayables = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(`${API_URL}/api/payables?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPayables(data);
      }
    } catch (error) {
      toast.error('Failed to load payables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayables();
  }, [statusFilter]);

  const handleApprove = async (payableId) => {
    try {
      const response = await fetch(`${API_URL}/api/payables/${payableId}/approve`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Payable approved');
        fetchPayables();
      }
    } catch (error) {
      toast.error('Failed to approve payable');
    }
  };

  const handleMarkPaid = async (payableId) => {
    try {
      const response = await fetch(`${API_URL}/api/payables/${payableId}/mark-paid`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Payable marked as paid');
        fetchPayables();
      }
    } catch (error) {
      toast.error('Failed to mark payable as paid');
    }
  };

  const openPaymentDialog = (payable) => {
    setSelectedPayable(payable);
    const amountDue = payable.total - (payable.amount_paid || 0);
    setPaymentForm({
      amount: amountDue.toFixed(2),
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'check',
      reference_number: '',
      notes: `Payment for ${payable.work_order_id}`
    });
    setShowPaymentDialog(true);
  };

  const handleRecordPayment = async () => {
    if (!selectedPayable) return;
    
    try {
      const response = await fetch(`${API_URL}/api/payments`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          payment_type: 'made',
          amount: parseFloat(paymentForm.amount),
          payment_date: paymentForm.payment_date,
          payment_method: paymentForm.payment_method,
          reference_number: paymentForm.reference_number,
          notes: paymentForm.notes,
          vendor_id: selectedPayable.vendor_id,
          payable_ids: [selectedPayable.payable_id]
        })
      });
      
      if (response.ok) {
        toast.success('Payment recorded successfully');
        setShowPaymentDialog(false);
        setSelectedPayable(null);
        fetchPayables();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to record payment');
      }
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="payables-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="payable-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
                <p className="text-2xl font-bold">
                  ${payables.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.total, 0).toLocaleString()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold">
                  ${payables.filter(p => p.status === 'approved').reduce((sum, p) => sum + p.total, 0).toLocaleString()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Paid Out</p>
                <p className="text-2xl font-bold">
                  ${payables.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.total, 0).toLocaleString()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : payables.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No payables found</h3>
              <p className="text-muted-foreground">Vendor payables will appear here when created</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payable ID</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Work Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payables.map((payable) => (
                  <TableRow key={payable.payable_id} data-testid={`payable-row-${payable.payable_id}`}>
                    <TableCell className="font-mono text-sm">{payable.payable_id}</TableCell>
                    <TableCell 
                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                      onClick={() => payable.vendor_id && navigate(`/vendors/${payable.vendor_id}`)}
                    >
                      {payable.vendor_name || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className="cursor-pointer hover:bg-blue-50"
                        onClick={() => navigate(`/work-orders/${payable.work_order_id}`)}
                      >
                        {payable.work_order_id}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(payable.status)}</TableCell>
                    <TableCell>{formatDate(payable.created_at)}</TableCell>
                    <TableCell className="text-right font-semibold">
                      ${payable.total?.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {payable.status === 'pending' && (
                            <DropdownMenuItem onClick={() => handleApprove(payable.payable_id)}>
                              <CheckCircle className="h-4 w-4 mr-2" /> Approve
                            </DropdownMenuItem>
                          )}
                          {payable.status === 'approved' && (
                            <>
                              <DropdownMenuItem 
                                onClick={() => openPaymentDialog(payable)}
                                data-testid={`record-payment-${payable.payable_id}`}
                              >
                                <ArrowUpRight className="h-4 w-4 mr-2" /> Record Payment
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleMarkPaid(payable.payable_id)}>
                                <DollarSign className="h-4 w-4 mr-2" /> Mark as Paid
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="h-5 w-5 text-red-600" />
              Record Payment to {selectedPayable?.vendor_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Work Order:</span>
                <span className="font-semibold">{selectedPayable?.work_order_id}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span>Payable Total:</span>
                <span className="font-semibold">${selectedPayable?.total?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span>Amount Paid:</span>
                <span className="font-semibold">${(selectedPayable?.amount_paid || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm mt-1 pt-1 border-t">
                <span>Balance Due:</span>
                <span className="font-bold text-red-600">
                  ${((selectedPayable?.total || 0) - (selectedPayable?.amount_paid || 0)).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    className="pl-7"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Method *</Label>
                <Select 
                  value={paymentForm.payment_method} 
                  onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {method.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference #</Label>
                <Input
                  value={paymentForm.reference_number}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
                  placeholder="Check #, Trans ID"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="Optional notes..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecordPayment}
              disabled={!paymentForm.amount || parseFloat(paymentForm.amount) <= 0}
            >
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payables;
